import React, { useEffect, useState } from "react";
import PrescriptionQueue from "../components/PrescriptionQueue";
import ApprovedPrescriptions from "../components/ApprovedPrescriptions";
import DeniedPrescriptions from "../components/DeniedPrescription";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { SERVER_URL } from "../config";
import "../styles/prescriptions/prescriptionContainer.css";
import { Prescription } from "../types/prescription";
import useProviderProfile from "../hooks/useProviderProfile";
import CreatePrescription from "../components/CreatePrescription";

const PrescriptionContainer: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    "pending" | "approved" | "denied" | "create"
  >("pending");
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const batchSize = 4; // Prefetch 4 prescriptions at a time
  const { provider } = useProviderProfile();

  useEffect(() => {
    if (provider) fetchPrescriptions(0);
  }, [provider]);

  // PrescriptionContainer.tsx  — inside fetchPrescriptions
  const fetchPrescriptions = async (newOffset: number) => {
    try {
      setLoading(true);

      const response = await fetchWithAuth(
        `${SERVER_URL}/api/prescription/nearby-prescriptions?limit=${batchSize}&offset=${newOffset}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch prescriptions");

      // accept either raw array or { data: [...] }
      const body = await response.json();
      const batch: Prescription[] = Array.isArray(body)
        ? body
        : body.data || [];

      setPrescriptions((prev) => [...prev, ...batch]);
      setOffset(newOffset + batchSize);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  if (provider === null) {
    return (
      <div>
        <h1> you need to be a provider to review prescriptions</h1>
      </div>
    );
  }

  if (loading && prescriptions.length === 0)
    return <p>Loading prescriptions...</p>;
  if (error) return <p style={{ color: "red" }}>Error: {error}</p>;

  // Map pending prescriptions to ensure medication has a products array

  const pendingPrescriptions: Prescription[] = prescriptions
    .filter((p) => p.status === "REQUESTED")
    .map<Prescription>((p) => ({
      ...p, // keeps id, patient, etc.
      medication: {
        ...p.medication,
        productPrices: (p.medication as any).productPrices || [],
      },
    }));

  const renderContent = () => {
    switch (activeTab) {
      case "pending":
        return (
          <PrescriptionQueue
            prescriptions={pendingPrescriptions}
            fetchNextBatch={() => fetchPrescriptions(offset)}
            provider={provider}
          />
        );
      case "create":
        return <CreatePrescription />;
      case "approved":
        return <ApprovedPrescriptions />;
      case "denied":
        return <DeniedPrescriptions />;
      default:
        return null;
    }
  };

  return (
    <div className="prescription-management-container">
      <div className="prescription-sidebar">
        <h2 className="prescription-management-title">
          Prescription Management
        </h2>
        <nav className="prescription-nav">
          <button
            className={`nav-button ${activeTab === "pending" ? "active" : ""}`}
            onClick={() => setActiveTab("pending")}
          >
            Pending
          </button>
          {/* <button
            className={`nav-button ${activeTab === "create" ? "active" : ""}`}
            onClick={() => setActiveTab("create")}
          >
            Create RX
          </button> */}
          <button
            className={`nav-button ${activeTab === "approved" ? "active" : ""}`}
            onClick={() => setActiveTab("approved")}
          >
            Approved
          </button>
          <button
            className={`nav-button ${activeTab === "denied" ? "active" : ""}`}
            onClick={() => setActiveTab("denied")}
          >
            Denied
          </button>
        </nav>
      </div>
      <div className="prescription-content">{renderContent()}</div>
    </div>
  );
};

export default PrescriptionContainer;
